<template>
  <div class="app-shell">
    <header class="app-shell__nav">
      <header-navigation-component />
    </header>

    <main class="app-shell__main">
      <child></child>
    </main>

    <footer class="app-shell__footer">
      <footer-component />
    </footer>
  </div>
</template>

<script>
import Child from "../Child";
import HeaderNavigationComponent from "@/pages/components/HeaderNavigationComponent.vue";
import FooterComponent from "@/pages/components/FooterComponent.vue";
import * as Sentry from "@sentry/vue";
import { mapGetters, mapState } from "vuex";

export default {
  components: {
    Child,
    HeaderNavigationComponent,
    FooterComponent,
  },
  computed: {
    ...mapGetters("auth", ["employeeCode", "isCurrentRoleEmployee", "isCurrentRoleResponsible"]),
    ...mapState("auth", ["auth"]),
  },
  created() {
    Sentry.setContext("auth", {
      ...this.auth,
      isCurrentRoleEmployee: this.isCurrentRoleEmployee,
      isCurrentRoleResponsible: this.isCurrentRoleResponsible,
    });
  },
};
</script>

<style lang="scss">
.color-primary {
  color: var(--portal-primary) !important;
}

.color-secondary {
  color: var(--portal-secondary) !important;
}

.app-shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--portal-bg);
  overflow-x: hidden;
}

.app-shell__nav {
  position: sticky;
  top: 0;
  z-index: 1030;
  background: var(--portal-nav-bg);
  border-bottom: 1px solid var(--portal-border);
  box-shadow: var(--portal-shadow-sm);
  overflow: visible;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background: radial-gradient(
      ellipse 120% 100% at 0% 0%,
      var(--portal-hero-glow) 0%,
      transparent 55%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.app-shell__main {
  flex: 1 0 auto;
  width: 100%;
  min-width: 0;
  min-height: 0;
  padding-left: 0;
  padding-right: 0;
}

.app-shell__footer {
  flex-shrink: 0;
  margin-top: auto;
}

[data-theme="light"] .shell-nav__logo {
  mix-blend-mode: multiply;
}

.main-nav-menu {
  border: none;
  background: transparent;
  box-shadow: none;
}

.main-top-menu {
  & a {
    color: var(--portal-text-muted) !important;
    font-weight: 600;
  }

  & .info {
    color: var(--portal-text);
  }
}

.main-bottom-menu-item {
  & > a {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--portal-text-muted) !important;
    font-size: 0.84rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    padding: 0.5rem 0.9rem !important;
    margin: 0;
    border-radius: 999px;
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

    i {
      font-size: 0.82rem;
      color: var(--portal-text-muted);
      transition: color 0.2s ease;
    }
  }

  & > a:hover {
    color: var(--portal-text) !important;
    background: var(--portal-surface-muted);
    border-bottom: none;
  }

  & > .active,
  & > a.router-link-exact-active,
  & > a.router-link-active {
    color: var(--portal-primary) !important;
    background: var(--portal-surface) !important;
    border-bottom: none !important;
    box-shadow: var(--portal-shadow-sm) !important;

    i {
      color: var(--portal-primary);
    }
  }
}

.dot {
  height: 25px;
  width: 25px;
  background-color: var(--portal-border);
  border-radius: 50%;
  display: inline-block;
}

.marker {
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
  text-transform: none;
}

.box-shadow {
  box-shadow: var(--portal-shadow-sm);
}

label.title {
  color: var(--portal-text-muted);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.4;
  text-transform: uppercase !important;
}

.custom-card {
  height: auto;
  min-height: 148px;
  border: 1px solid var(--portal-border);
  border-radius: 16px;
  width: 100%;
  background: var(--portal-surface);
  box-shadow: var(--portal-shadow-sm);

  & > .card-body .card-title {
    color: var(--portal-text-muted);
    font-size: 0.84rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.35;
  }

  & .card-text {
    color: var(--portal-text);
    font-size: 2.35rem;
    font-weight: 700;
    letter-spacing: -0.04em;
    line-height: 1;
  }
}

.plan {
  .plan-bar {
    border-radius: 14px;
    background-color: var(--portal-surface);
    box-shadow: var(--portal-shadow-sm);
    border: 1px solid var(--portal-border);
  }

  .plan-card {
    height: 180px;
    width: 150px;
    border-radius: 14px;

    & > .card-body {
      padding: 0 !important;
      margin: 0 !important;
    }
  }

  .day {
    color: var(--portal-primary);
    background-color: var(--portal-primary-soft);
    border-radius: 0 0 12px 0;

    .number {
      font-size: 1.1rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      line-height: 1;
      text-align: center;
      padding: 0.7rem 0.75rem;
    }
  }

  .plan-text-header {
    color: var(--portal-text-muted);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0;
  }

  .plan-text-body {
    color: var(--portal-text);
    font-size: 0.88rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0;
  }

  .plan-day-body {
    margin: 0.85rem !important;
  }

  .plan-day-header {
    & > img {
      margin-top: 0.5rem !important;
      margin-right: 0.5rem !important;
    }
  }
}

.permission-form {
  & label.title {
    color: var(--portal-text-muted);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: 1.4;
    text-transform: uppercase !important;
  }
}

.permission-calendar {
  & .month-title {
    color: var(--portal-text);
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
    text-align: center;
  }

  & > .card {
    border: 1px solid var(--portal-border) !important;
    border-radius: 14px !important;
    background: var(--portal-surface-muted) !important;
    box-shadow: none !important;
    margin-bottom: 0.85rem;
  }

  & > .card .card-title {
    border-bottom: 1px solid var(--portal-border) !important;
    padding: 0.35rem 0.5rem;
  }

  & .btn-refresh {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    color: var(--portal-text-muted) !important;
    transition: background 0.2s ease, color 0.2s ease;

    &:hover {
      background: var(--portal-primary-soft);
      color: var(--portal-primary) !important;
    }
  }

  & .day {
    max-width: none;
    border: 1px solid var(--portal-border);
    border-radius: 12px;
    box-shadow: none;
    background: var(--portal-surface);

    & > .day-header,
    .active {
      background-color: var(--portal-surface-muted);
      border-radius: 12px 12px 0 0;
    }

    & > .active {
      background-color: var(--portal-primary);
      border: 1px solid var(--portal-primary);

      & > span {
        color: $color-white;
      }
    }

    & .day-body > img {
      margin: 5px;
    }
  }

  & .day-number {
    color: var(--portal-text-muted);
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
    text-align: center;
    padding: 0;
    margin: 0;
  }

  & .day-legend {
    color: var(--portal-text-muted);
    font-size: 0.88rem;
    font-weight: 600;

    & > span {
      margin-left: 4px;
      color: inherit;
    }

    img {
      vertical-align: middle;
    }
  }

  .table-without-border td,
  .table-without-border > thead th {
    border: 0;
  }

  .table-without-border > thead td {
    padding: 0.35rem 0.25rem 0.65rem;
    color: var(--portal-text-muted);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

form label,
.label-description {
  color: var(--portal-text-muted);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.4;
  text-transform: uppercase;
}

form .description {
  color: var(--portal-text);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 22.4px;
  height: 22.4px;
}

form .label-description {
  margin: 0;
}

.secondary-button {
  color: var(--portal-secondary);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
}
</style>
